/**
 * The view store (task 9.3).
 *
 * Zustand holds *view* state; the engine holds *game* state; the two never
 * merge (design.md D15). Nothing here is a `GameState`, an `Action`, or a
 * `Trace` — an in-progress run is described by a `RunSummary` for the title
 * screen's benefit and nothing more. The game itself lives behind `useEngine`,
 * which owns it for exactly as long as a run is on screen.
 *
 * The store is also free of mission knowledge: no mission ids, no node names,
 * no channel names. `shell-has-no-mission-knowledge.test.ts` asserts that of
 * this whole directory tree against the real packages in `content/`.
 */
import { create } from 'zustand';
import { defaultDriver, openSaveStore, type SaveStore } from '../lib/save/store.js';
import type { UpdateBridge } from '../lib/pwa/updates.js';
import {
  addProfile,
  deleteProfile,
  dismissOnboarding,
  parseImport,
  purchasePerk,
  recordResult,
  recordTutorialCompleted,
  renameProfile,
  setPerkEquipped,
  setRun,
  setSettings,
  switchProfile,
  type ImportSummary,
} from '../lib/save/document.js';
import {
  activeProfileOf,
  type MissionResult,
  type SavedRun,
  type SaveDocument,
  type SaveProfile,
} from '../lib/save/types.js';
import { applySettings, type ShellSettings } from '../lib/settings.js';

/** A message queued for the polite live region (task 9.11). */
export interface Announcement {
  /** Monotonic, so repeating the same text still re-announces. */
  readonly key: number;
  readonly text: string;
}

export interface StorageStatus {
  /** Nothing written this session will survive a reload. */
  readonly ephemeral: boolean;
  /** Stored data was unreadable or refused, and a fresh document was started. */
  readonly recovered: boolean;
  /** Why, in the player's words. Set whenever `recovered` is. */
  readonly reason: string | null;
  /** The one-time warning has been shown (`save-system` spec, "warned once"). */
  readonly warned: boolean;
  /** The save format a migration brought the document forward from, if any. */
  readonly migratedFrom: number | null;
}

const NO_STORAGE_ISSUE: StorageStatus = {
  ephemeral: false,
  recovered: false,
  reason: null,
  warned: false,
  migratedFrom: null,
};

/** A new build, installed and waiting for the player's say-so (task 14.2). */
export interface UpdateStatus {
  /** A newer build is installed and waiting. */
  readonly ready: boolean;
  /** The player accepted; the run is being flushed before the reload. */
  readonly applying: boolean;
  /** The player said not now. The prompt stays gone until the next build.  */
  readonly dismissed: boolean;
}

const NO_UPDATE: UpdateStatus = { ready: false, applying: false, dismissed: false };

export interface ViewState {
  readonly ready: boolean;
  readonly doc: SaveDocument | null;
  readonly storage: StorageStatus;
  readonly update: UpdateStatus;
  readonly announcement: Announcement | null;
  /** The sidebar is a drawer below the layout breakpoint (task 9.10). */
  readonly drawerOpen: boolean;
  /** How to Play opens over whatever is on screen, advancing no game time. */
  readonly helpOpen: boolean;

  init(saveStore?: SaveStore, now?: string): Promise<void>;
  announce(text: string): void;
  clearAnnouncement(): void;
  setDrawerOpen(open: boolean): void;
  setHelpOpen(open: boolean): void;
  acknowledgeStorageWarning(): void;

  updateSettings(settings: ShellSettings): void;
  createProfile(name: string): void;
  renameActiveProfile(name: string): void;
  selectProfile(profileId: string): void;
  removeProfile(profileId: string): void;
  resetEverything(): void;
  importSave(text: string): ImportSummary;
  /**
   * Persists the in-progress run, or clears it. Called after every
   * state-changing action (task 13.2).
   */
  setRun(run: SavedRun | null): void;
  /** Records a finished run and banks the Corporate Experience it improved on. */
  recordMissionResult(missionId: string, difficultyProfileId: string, result: MissionResult): void;
  /** Remembers that a contextual onboarding prompt has been dismissed. */
  dismissOnboarding(promptId: string): void;
  /**
   * Remembers that a mission's guided walkthrough was walked to its end.
   *
   * Separate from `recordMissionResult` on purpose: a tutorial banks nothing
   * and unlocks nothing, so this writes no progress record (design D4, D9).
   */
  recordTutorialCompleted(missionId: string): void;
  buyPerk(perkId: string, cost: number): void;
  setPerkEquipped(perkId: string, equipped: boolean, equipSlots: number): void;

  /** A new build has installed and is waiting to be applied. */
  updateAvailable(bridge: UpdateBridge): void;
  /** Flushes whatever is in progress, then applies the waiting build. */
  applyUpdate(): Promise<void>;
  dismissUpdate(): void;
}

let saveStore: SaveStore | null = null;
let updateBridge: UpdateBridge | null = null;
let announcementKey = 0;

const nowIso = (): string => new Date().toISOString();

/** Pushes the active profile's settings at the document. */
function paint(doc: SaveDocument): void {
  if (typeof document === 'undefined') return;
  applySettings(document.documentElement, activeProfileOf(doc).settings);
}

export const useViewStore = create<ViewState>()((set, get) => {
  const commit = (doc: SaveDocument): void => {
    saveStore?.save(doc);
    paint(doc);
    set({ doc });
  };

  /**
   * Applies `change` to the active profile, and does nothing at all when the
   * change was a no-op.
   *
   * The identity check is load-bearing rather than tidy: `setRun` runs after
   * every action in a mission, and without it a refused or unchanged write
   * would still repaint the document element and re-render every subscriber
   * once per keystroke's worth of play.
   */
  const withActive = (change: (doc: SaveDocument, profile: SaveProfile) => SaveDocument): void => {
    const { doc } = get();
    if (!doc) return;
    const next = change(doc, activeProfileOf(doc));
    if (next !== doc) commit(next);
  };

  return {
    ready: false,
    doc: null,
    storage: NO_STORAGE_ISSUE,
    update: NO_UPDATE,
    announcement: null,
    drawerOpen: false,
    helpOpen: false,

    init: async (injected, now = nowIso()) => {
      saveStore = injected ?? openSaveStore(defaultDriver());
      const loaded = await saveStore.load(now);
      paint(loaded.doc);
      set({
        ready: true,
        doc: loaded.doc,
        storage: {
          ephemeral: loaded.ephemeral,
          recovered: loaded.recovered,
          reason: loaded.recoveredBecause,
          warned: false,
          migratedFrom: loaded.migratedFrom,
        },
      });
    },

    announce: (text) => {
      announcementKey += 1;
      set({ announcement: { key: announcementKey, text } });
    },
    clearAnnouncement: () => {
      set({ announcement: null });
    },
    setDrawerOpen: (drawerOpen) => {
      set({ drawerOpen });
    },
    setHelpOpen: (helpOpen) => {
      set({ helpOpen });
    },
    acknowledgeStorageWarning: () => {
      set((state) => ({ storage: { ...state.storage, warned: true } }));
    },

    updateSettings: (settings) => {
      withActive((doc, profile) => setSettings(doc, profile.id, settings));
    },
    createProfile: (name) => {
      withActive((doc) => addProfile(doc, name, nowIso()));
    },
    renameActiveProfile: (name) => {
      withActive((doc, profile) => renameProfile(doc, profile.id, name));
    },
    selectProfile: (profileId) => {
      withActive((doc) => switchProfile(doc, profileId));
    },
    removeProfile: (profileId) => {
      withActive((doc) => deleteProfile(doc, profileId, nowIso()));
    },
    resetEverything: () => {
      if (!saveStore) return;
      const doc = saveStore.reset(nowIso());
      paint(doc);
      set({ doc, storage: NO_STORAGE_ISSUE });
    },
    importSave: (text) => {
      const { doc, summary } = parseImport(text);
      commit(doc);
      return summary;
    },
    setRun: (run) => {
      withActive((doc, profile) => setRun(doc, profile.id, run));
    },
    recordMissionResult: (missionId, difficultyProfileId, result) => {
      withActive((doc, profile) =>
        recordResult(doc, profile.id, missionId, difficultyProfileId, result),
      );
    },
    dismissOnboarding: (promptId) => {
      withActive((doc, profile) => dismissOnboarding(doc, profile.id, promptId));
    },
    recordTutorialCompleted: (missionId) => {
      withActive((doc, profile) => recordTutorialCompleted(doc, profile.id, missionId));
    },
    buyPerk: (perkId, cost) => {
      withActive((doc, profile) => purchasePerk(doc, profile.id, perkId, cost));
    },
    setPerkEquipped: (perkId, equipped, equipSlots) => {
      withActive((doc, profile) => setPerkEquipped(doc, profile.id, perkId, equipped, equipSlots));
    },

    updateAvailable: (bridge) => {
      updateBridge = bridge;
      // A newly waiting build clears an earlier dismissal: the player declined
      // *that* update, not every future one.
      set({ update: { ready: true, applying: false, dismissed: false } });
    },

    applyUpdate: async () => {
      const bridge = updateBridge;
      if (bridge === null) return;
      set((state) => ({ update: { ...state.update, applying: true } }));
      // The whole point of prompting rather than taking over: the run in
      // progress reaches disk before the page is replaced. `save` is
      // fire-and-forget by design, so flushing is what makes that a promise
      // rather than a hope.
      try {
        await saveStore?.flush();
      } catch {
        // A device that cannot write is one the player has already been warned
        // about. It must not be able to block an update.
      }
      await bridge.apply();
    },

    dismissUpdate: () => {
      set((state) => ({ update: { ...state.update, ready: false, dismissed: true } }));
    },
  };
});

/** The active save profile, or `null` before `init` has run. */
export function useActiveProfile(): SaveProfile | null {
  const doc = useViewStore((state) => state.doc);
  return doc ? activeProfileOf(doc) : null;
}
